import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { Suspense, lazy, useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Camera, Pencil, Sparkles, Cpu, Droplet } from "lucide-react";
import { api } from "~/lib/api";
import { openStream, type Frame } from "~/lib/stream";
import { useMe, isRole } from "~/lib/auth";
import { AppShell } from "~/components/AppShell";
import { Button } from "~/components/ui/Button";
import { Group, GroupHeader } from "~/components/ui/Group";
import { Row, RowSeparator } from "~/components/ui/Row";
import { IconBadge } from "~/components/ui/IconBadge";
import { useToast } from "~/components/ui/Toast";

const ScannerSheet = lazy(() =>
  import("~/components/ScannerSheet").then((m) => ({ default: m.ScannerSheet })),
);

export const Route = createFileRoute("/_authed/claim")({
  component: ClaimPage,
});

function extractDeviceID(raw: string): string | null {
  const trimmed = raw.trim();
  if (!trimmed) return null;
  const m = trimmed.match(/^sqi:\/\/device\/([0-9a-f]{8,32})$/i);
  if (m) return m[1].toLowerCase();
  if (/^[0-9a-f]{8,32}$/i.test(trimmed)) return trimmed.toLowerCase();
  return null;
}

function ClaimPage() {
  const navigate = useNavigate();
  const toast = useToast();
  const { data: me } = useMe();

  const canDiscover = isRole(me, "admin", "support");
  const [manual, setManual] = useState("");
  const [showManual, setShowManual] = useState(false);
  const [discovered, setDiscovered] = useState<Frame[]>([]);
  const [scanning, setScanning] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!canDiscover) return;
    const handle = openStream("/stream/unclaimed", (f) => {
      if (f.kind !== "info") return;
      setDiscovered((prev) => {
        if (prev.find((p) => p.device_id === f.device_id)) return prev;
        return [...prev, f];
      });
    });
    return () => handle.close();
  }, [canDiscover]);

  async function claim(deviceID: string) {
    setBusy(true);
    try {
      await api.claimDevice(deviceID);
      navigate({ to: "/$deviceId", params: { deviceId: deviceID } });
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "We couldn't add that device. Try again.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <AppShell back={{ to: "/", label: "Devices" }} showTabBar={false}>
      <div className="max-w-md mx-auto pt-4 sm:pt-6 lg:pt-10">
        <header className="pb-6">
          <h1 className="text-largeTitle" style={{ color: "var(--label-primary)" }}>
            Add Device
          </h1>
          <div className="text-subhead mt-1" style={{ color: "var(--label-secondary)" }}>
            Scan the QR code on the back of your device.
          </div>
        </header>

        <div className="space-y-5">
          <Button
            size="lg"
            onClick={() => setScanning(true)}
            disabled={busy}
            leadingIcon={<Camera size={18} />}
            className="w-full"
          >
            Scan QR code
          </Button>

          <AnimatePresence>
            {discovered.length > 0 && (
              <motion.div
                key="discovered"
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                transition={{ duration: 0.2 }}
              >
                <GroupHeader>
                  <span className="inline-flex items-center gap-1.5">
                    <Sparkles size={12} />
                    {discovered.length === 1
                      ? "Found 1 device near you"
                      : `Found ${discovered.length} devices near you`}
                  </span>
                </GroupHeader>
                <Group>
                  {discovered.map((d, i) => {
                    const isAquagate = d.device_type === "aquagate";
                    return (
                      <div key={d.device_id}>
                        <Row
                          onClick={() => claim(d.device_id)}
                          leading={
                            <IconBadge tint={isAquagate ? "blue" : "gray"} size={28}>
                              {isAquagate ? <Droplet size={15} /> : <Cpu size={15} />}
                            </IconBadge>
                          }
                          title={isAquagate ? "New Aquagate" : "New device"}
                          subtitle={
                            <span className="font-mono">{d.device_id.slice(0, 12)}…</span>
                          }
                          detail={<span style={{ color: "var(--system-blue)", fontWeight: 600 }}>Add</span>}
                        />
                        {i < discovered.length - 1 && <RowSeparator inset={56} />}
                      </div>
                    );
                  })}
                </Group>
              </motion.div>
            )}
          </AnimatePresence>

          <div>
            {!showManual && (
              <button
                type="button"
                onClick={() => setShowManual(true)}
                className="w-full text-callout flex items-center justify-center gap-2 py-2 transition-opacity active:opacity-60"
                style={{ color: "var(--system-blue)" }}
              >
                <Pencil size={14} />
                Have a code? Enter it manually
              </button>
            )}

            <AnimatePresence>
              {showManual && (
                <motion.form
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.2 }}
                  onSubmit={(e) => {
                    e.preventDefault();
                    const id = extractDeviceID(manual);
                    if (id) claim(id);
                    else toast.error("That code doesn't look like a SquaredIoT device ID.");
                  }}
                  className="space-y-3 overflow-hidden"
                >
                  <div className="px-1 text-footnote uppercase tracking-[0.04em]" style={{ color: "var(--label-secondary)" }}>
                    Device code
                  </div>
                  <input
                    value={manual}
                    onChange={(e) => setManual(e.target.value)}
                    placeholder="0123abcd…"
                    autoFocus
                    className="w-full text-body font-mono focus:outline-none"
                    style={{
                      background: "var(--fill-tertiary)",
                      border: "0.5px solid var(--separator)",
                      color: "var(--label-primary)",
                      height: 50,
                      paddingInline: 16,
                      borderRadius: 12,
                    }}
                  />
                  <Button type="submit" size="lg" loading={busy} disabled={!manual.trim()} className="w-full">
                    Add device
                  </Button>
                </motion.form>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>

      {scanning && (
        <Suspense fallback={null}>
          <ScannerSheet
            onCancel={() => setScanning(false)}
            onScan={(text) => {
              setScanning(false);
              const id = extractDeviceID(text);
              if (id) claim(id);
              else toast.error("That QR code isn't from a SquaredIoT device.");
            }}
          />
        </Suspense>
      )}
    </AppShell>
  );
}
